import { useLazyUser } from "$global-providers/user-provider";
import { gql } from "@apollo/client";
import type {
  EpocusEncounterSyncPushDocument,
  EpocusReviewOutcome,
  EpocusRequestPriority as GqlEpocusRequestPriority,
  QueryPullEpocusEncounterArgs,
} from "elephant-web/gql";
import { useMemo } from "react";
import type { RxCollection, RxDatabase, WithDeleted } from "rxdb";
import type { EpocusEncounter } from "../collections/epocus-encounter";
import type { RxDBCollections } from "../provider";
import {
  SyncEpocusEncounterPullDocument,
  SyncEpocusEncounterPushDocument,
  type WholeSyncableEpocusEncounterFragment,
} from "./__generated__/epocus-encounter.gql";
import { createPersistedCheckpointPullBuilders } from "./create-persisted-checkpoint-pull-builders";
import {
  EPOCUS_ENCOUNTER_PULL_ROLES,
  EPOCUS_ENCOUNTER_PUSH_ROLES,
  isReplicationRoleAllowed,
} from "./replication-role-allowlist";
import {
  type ReplicationPullConfig,
  useGqlReplication,
} from "./use-gql-replication";

export const useReplicateEpocusEncounter = ({
  db,
  collection,
}: {
  db?: RxDatabase<RxDBCollections>;
  collection?: RxCollection<EpocusEncounter>;
}) => {
  const role = useLazyUser()?.role;
  const pullAllowed = isReplicationRoleAllowed(
    EPOCUS_ENCOUNTER_PULL_ROLES,
    role,
  );
  const pushAllowed = isReplicationRoleAllowed(
    EPOCUS_ENCOUNTER_PUSH_ROLES,
    role,
  );

  const push = useMemo(
    () => ({
      mutation: SyncEpocusEncounterPushDocument,
      mapRxToGql: mapRxEpocusEncounterToGqlInput,
    }),
    [],
  );

  const pull = useMemo<
    ReplicationPullConfig<WholeSyncableEpocusEncounterFragment>
  >(
    () => ({
      query: SyncEpocusEncounterPullDocument,
      reSyncInterval: { minutes: 5 },
      ...createPersistedCheckpointPullBuilders<
        QueryPullEpocusEncounterArgs,
        WholeSyncableEpocusEncounterFragment
      >({
        buildInputVariables: (checkpoint, batchSize) => ({
          input: {
            limit: batchSize,
            checkpoint,
          },
        }),
        buildPullCheckpoint: (doc) => ({
          id: doc.id,
          updatedAt: doc.meta.updatedAt,
        }),
        entityId: "global",
        db,
        collectionName: collection?.name,
      }),
    }),
    [db, collection],
  );

  return useGqlReplication<
    EpocusEncounter,
    WholeSyncableEpocusEncounterFragment,
    EpocusEncounterSyncPushDocument
  >({
    db,
    collection,
    push: pushAllowed ? push : undefined,
    pull: pullAllowed ? pull : undefined,
    mapGqlToRx: mapGqlEpocusEncounterToRx,
    batchSize: 100,
    expireData: false,
    metadataLocalStorageKeySuffix: "epocus-encounter",
    enabled: pullAllowed || pushAllowed,
  });
};

export const mapGqlEpocusEncounterToRx = (
  doc: WholeSyncableEpocusEncounterFragment,
): WithDeleted<EpocusEncounter> => ({
  id: doc.id,
  elephantPatientId: doc.elephantPatientId ?? null,
  patientName: doc.patientName ?? null,
  patientDob: doc.patientDob ?? null,
  patientGender: doc.patientGender ?? null,
  operatorName: doc.operatorName ?? null,
  accessionNumber: doc.accessionNumber ?? null,
  dicomStudyId: doc.dicomStudyId ?? null,
  patientMrn: doc.patientMrn ?? null,
  indication: doc.indication ?? null,
  requestedById: doc.requestedById ?? null,
  requestedAt: doc.requestedAt ?? null,
  requestedExamType: doc.requestedExamType ?? null,
  requestPriority: doc.requestPriority
    ? (doc.requestPriority.toLowerCase() as EpocusEncounter["requestPriority"])
    : null,
  requestNotes: doc.requestNotes ?? null,
  status: doc.status.toLowerCase() as EpocusEncounter["status"],
  performerId: doc.performerId ?? null,
  programId: doc.programId ?? null,
  deviceId: doc.deviceName ?? null,
  deviceSerialNumber: doc.deviceSerialNumber ?? null,
  location: doc.location ?? null,
  submittedForReviewAt: doc.submittedForReviewAt ?? null,
  assignedReviewerId: doc.assignedReviewerId ?? null,
  assignedAt: doc.assignedAt ?? null,
  reviewOutcome: doc.reviewOutcome
    ? (doc.reviewOutcome.toLowerCase() as EpocusEncounter["reviewOutcome"])
    : null,
  escalatedAt: doc.escalatedAt ?? null,
  escalationReason: doc.escalationReason ?? null,
  date: doc.encounterDate ?? doc.meta.createdAt,
  meta: {
    createdAt: doc.meta.createdAt,
    updatedAt: doc.meta.updatedAt,
    createdByUserId: doc.meta.createdByUserId,
    updatedByUserId: doc.meta.updatedByUserId,
  },
  _deleted: doc.meta.deleted,
});

export const mapRxEpocusEncounterToGqlInput = (
  doc: WithDeleted<EpocusEncounter>,
): EpocusEncounterSyncPushDocument => ({
  id: doc.id,
  accessionNumber: doc.accessionNumber,
  elephantPatientId: doc.elephantPatientId,
  patientName: doc.patientName,
  status: doc.status.toUpperCase(),
  performerId: doc.performerId,
  programId: doc.programId ?? null,
  deviceName: doc.deviceId,
  location: doc.location,
  encounterDate: doc.date,
  dicomStudyId: doc.dicomStudyId,
  patientMrn: doc.patientMrn,
  indication: doc.indication,
  requestedById: doc.requestedById,
  requestedAt: doc.requestedAt,
  requestedExamType: doc.requestedExamType,
  requestPriority: doc.requestPriority
    ? (doc.requestPriority.toUpperCase() as GqlEpocusRequestPriority)
    : null,
  requestNotes: doc.requestNotes,
  deviceSerialNumber: doc.deviceSerialNumber,
  patientDob: doc.patientDob,
  patientGender: doc.patientGender,
  operatorName: doc.operatorName,
  submittedForReviewAt: doc.submittedForReviewAt,
  assignedReviewerId: doc.assignedReviewerId,
  assignedAt: doc.assignedAt,
  reviewOutcome: doc.reviewOutcome
    ? (doc.reviewOutcome.toUpperCase() as EpocusReviewOutcome)
    : null,
  escalatedAt: doc.escalatedAt,
  escalationReason: doc.escalationReason,
  // examTypes and imageCount are required by the GQL input type but are not
  // tracked in the local encounter schema (managed server-side via exam
  // replication). Send safe placeholder values to satisfy the mutation.
  // TODO(PNE-XXX): derive these from the epocus-exam collection at push time.
  examTypes: [],
  imageCount: 0,
  meta: {
    createdAt: doc.meta.createdAt,
    updatedAt: doc.meta.updatedAt,
    createdByUserId: doc.meta.createdByUserId,
    updatedByUserId: doc.meta.updatedByUserId,
    deleted: doc._deleted,
  },
});

gql`
  fragment WholeSyncableEpocusEncounter on SyncableEpocusEncounter {
    id
    elephantPatientId
    patientName
    patientDob
    patientGender
    operatorName
    accessionNumber
    dicomStudyId
    patientMrn
    indication
    requestedById
    requestedAt
    requestedExamType
    requestPriority
    requestNotes
    status
    performerId
    programId
    deviceName
    deviceSerialNumber
    location
    submittedForReviewAt
    assignedReviewerId
    assignedAt
    reviewOutcome
    escalatedAt
    escalationReason
    encounterDate
    meta {
      createdAt
      updatedAt
      createdByUserId
      updatedByUserId
      deleted
    }
  }
`;

gql`
  query SyncEpocusEncounterPull($input: PullEpocusEncounterInput!) {
    pullEpocusEncounter(input: $input) {
      checkpoint {
        id
        updatedAt
      }
      documents {
        ...WholeSyncableEpocusEncounter
      }
    }
  }
`;

gql`
  mutation SyncEpocusEncounterPush($input: PushEpocusEncounterInput!) {
    pushEpocusEncounter(input: $input) {
      rows {
        ...WholeSyncableEpocusEncounter
      }
    }
  }
`;
